import { Resource } from 'constants/resources';

import get from 'lodash/get';
import { useGetOne } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { FilterChipLabel } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/FilterChipLabel/FilterChipLabel';
import { PopoverChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/PopoverChip/PopoverChip';

export type FilterAutocompleteChipProps = {
  label: string;
  resource: Resource;
  name: string;
  children: React.ReactNode;
  onDelete?: () => void;
  onApply?: () => void;
  onReset?: () => void;
  disabled?: boolean;
  optionText?: string;
};

export const FilterAutocompleteChip: React.FC<FilterAutocompleteChipProps> = ({
  resource,
  name,
  label,
  children,
  onApply,
  onDelete,
  onReset,
  disabled,
  optionText,
}) => {
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const { getValues, setValue } = useFormContext();

  const { data, isLoading } = useGetOne(
    resource,
    { id: get(appliedFilters, name) },
    { enabled: Boolean(get(appliedFilters, name)) },
  );

  if (!get(appliedFilters, name)) {
    return <></>;
  }

  const value = data?.[optionText || 'name'];

  function onApplyHandler() {
    handleClose();
    onApply && onApply();
    setAppliedFilter(name, getValues(name));
  }

  function onResetHandler() {
    onReset && onReset();
    setAppliedFilter(name, appliedFilters?.[name]);
    setValue(name, appliedFilters?.[name]);
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <PopoverChip
        anchorEl={anchorEl}
        onApply={onApplyHandler}
        onClose={handleClose}
        onReset={onResetHandler}
        open={open}
      >
        {children}
      </PopoverChip>
      <Chip
        label={<FilterChipLabel isLoading={isLoading} label={label} value={value} />}
        onClick={!disabled ? handleClick : undefined}
        onDelete={() => {
          onDelete && onDelete();
          setValue(name, null);
          setAppliedFilter(name, null);
        }}
        size="medium"
      />
    </>
  );
};
